import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'
import {
    ServedSchoolPatchItem,
    ServedSchoolsResponse,
} from '../models/Company/types'

export function servedSchoolsQueryKey(apartmentId: number) {
    return ['served-schools', apartmentId] as const
}

export function useServedSchools(apartmentId: number | undefined) {
    const queryClient = useQueryClient()

    const query = useQuery<ServedSchoolsResponse>({
        queryKey: servedSchoolsQueryKey(apartmentId ?? 0),
        queryFn: async () => {
            const res = await axiosInstance.get(
                `/company/apartment/${apartmentId}/served-schools/`,
            )
            return res.data
        },
        enabled: apartmentId != null,
    })

    const saveMutation = useMutation({
        mutationFn: async (schools: ServedSchoolPatchItem[]) => {
            const res = await axiosInstance.patch(
                `/company/apartment/${apartmentId}/served-schools/`,
                { schools },
            )
            return res.data as ServedSchoolsResponse
        },
        onSuccess: (data) => {
            if (apartmentId != null) {
                queryClient.setQueryData(
                    servedSchoolsQueryKey(apartmentId),
                    data,
                )
            }
        },
        onError: (err: { response?: { data?: { error?: string } } }) => {
            const msg =
                err.response?.data?.error ?? 'Failed to update schools served'
            toast.error(msg)
        },
    })

    const addSchoolMutation = useMutation({
        mutationFn: async (schoolId: number) => {
            const res = await axiosInstance.post(
                `/company/apartment/${apartmentId}/served-schools/`,
                { school_id: schoolId },
            )
            return res.data as ServedSchoolsResponse
        },
        onSuccess: (data) => {
            if (apartmentId != null) {
                queryClient.setQueryData(
                    servedSchoolsQueryKey(apartmentId),
                    data,
                )
            }
            toast.success('School added')
        },
        onError: () => {
            toast.error('Failed to add school')
        },
    })

    return {
        servedSchools: query.data,
        isLoading: query.isLoading,
        isSaving: saveMutation.isLoading || addSchoolMutation.isLoading,
        saveServedSchools: saveMutation.mutateAsync,
        addServedSchool: addSchoolMutation.mutateAsync,
        refetch: query.refetch,
    }
}
